import { roomData } from './data';
import { IMatrixRoomInfo, IMatrixMessage } from './types';
import { matrixClient } from '../client';
import { messageUtils } from '../message/messageUtils';
import { memberUtils } from './memberUtils';
import { Log } from '@fanfanlo';

const fileLogger = new Log(false, 'im.client.room.roomService');
// fileLogger.childrenPaused = true;
// fileLogger.pause = true;
export const roomService = {
    // 获取已加入房间的ID集合
    getJoinedRoomIds() {
        return roomData.joinedRoomIds;
    },

    // 获取已加入的房间列表
    getJoinedRooms() {
        return roomData.joinedRooms;
    },

    // 添加房间到已加入列表
    addJoinedRoom(roomId: string) {
        roomData.joinedRoomIds.add(roomId);
        // 加载房间成员
        this.loadRoomMembers(roomId);
    },

    // 从已加入列表中移除房间
    removeJoinedRoom(roomId: string) {
        roomData.joinedRoomIds.delete(roomId);
        const index = roomData.joinedRooms.findIndex((r: IMatrixRoomInfo) => r.roomId === roomId);
        if (index !== -1) {
            roomData.joinedRooms.splice(index, 1);
        }
    },

    // 查找房间信息
    findRoomInfo(roomId: string): IMatrixRoomInfo | undefined {
        return roomData.joinedRooms.find(r => r.roomId === roomId);
    },

    // 添加或更新房间信息
    addOrUpdateRoomInfo(roomInfo: IMatrixRoomInfo) {
        const index = roomData.joinedRooms.findIndex(r => r.roomId === roomInfo.roomId);
        if (index === -1) {
            roomData.joinedRooms.push(roomInfo);
        } else {
            Object.assign(roomData.joinedRooms[index], roomInfo);
        }
        return roomInfo;
    },

    // 消息相关方法
    async loadRoomMessages(roomId: string, limit = 50) {
        try {
            const room = matrixClient.client.getRoom(roomId);
            if (!room) {
                console.error('Room not found:', roomId);
                return [];
            }

            const events = await matrixClient.client.scrollback(room, limit);
            
            if (events && events.length > 0) {
                messageUtils.clearMessages(roomId);
                events.reverse().forEach((event: any) => {
                    if (event.getType() === 'm.room.message') {
                        this.addMessage(roomId, event);
                    }
                });
            }
            return messageUtils.getMessages(roomId);
        } catch (error) {
            console.error('Failed to load room messages:', error);
            return [];
        }
    },

    addMessage(roomId: string, event: any) {
        const eventType = event.getType();
        const content = event.getContent();
        
        // 处理位置消息
        if (eventType === 'm.room.message' && content.msgtype === 'm.location') {
            return this.handleLocationMessage(roomId, {
                ...event.event,
                content: content
            });
        }
        
        // 处理普通消息
        const message: IMatrixMessage = {
            id: event.getId(),
            roomId,
            sender: event.getSender(),
            content: content,
            timestamp: event.getTs(),
            type: eventType,
            status: 'received',
            event
        };
        
        messageUtils.addMessage(roomId, message);
        return message;
    },

    async sendTextMessage(roomId: string, text: string) {
        try {
            const response = await matrixClient.client.sendTextMessage(roomId, text);
            return response.event_id;
        } catch (error) {
            console.error('Failed to send message:', error);
            throw error;
        }
    },

    // 发送位置消息
    async sendLocationMessage(
        roomId: string,
        latitude: number,
        longitude: number,
        title: string = '',
        description: string = ''
    ) {
        try {
            const content = {
                msgtype: 'm.location',
                body: title || `位置: ${latitude}, ${longitude}`,
                geo_uri: `geo:${latitude},${longitude}`,
                info: {
                    title,
                    description
                }
            };
            
            const response = await matrixClient.client.sendMessage(roomId, content);
            return response.event_id;
        } catch (error) {
            console.error('Failed to send location message:', error);
            throw error;
        }
    },
    
    // 处理接收到的位置消息
    handleLocationMessage(roomId: string, event: any) {
        const message: IMatrixMessage = {
            id: event.event_id,
            type: 'm.location',
            sender: event.sender,
            timestamp: event.origin_server_ts,
            roomId,
            content: {
                msgtype: 'm.location',
                body: event.content.body,
                geo_uri: event.content.geo_uri,
                info: event.content.info
            },
            status: 'received'
        };
        
        messageUtils.addMessage(roomId, message);
        return message;
    },

    // 成员相关方法
    async loadRoomMembers(roomId: string) {
        const logger = fileLogger.sub(false, 'loadRoomMembers_fn');
        // logger.pause = true;
        try {
            const room = await matrixClient.client.getRoom(roomId);
            logger.log('room=', room)
            if (!room) {
                console.error('Room not found:', roomId);
                return [];
            }
            const members = room.getJoinedMembers();
            logger.log('members=', members)
            const memberList = members.map((member: any) => ({
                userId: member.userId,
                displayName: member.rawDisplayName || member.name,
                avatarUrl: member.getMxcAvatarUrl(),
                membership: 'join' as const
            }));
            logger.log('memberList=', memberList)
            memberUtils.setRoomMembers(roomId, memberList);
            return memberList;
        } catch (error) {
            console.error('Failed to load room members:', error);
            return [];
        }
    },
    
    getRoomMembers(roomId: string) {
        return memberUtils.getRoomMembers(roomId);
    }
};
